import type { ReactNode } from 'react';
import logoEmblem from '@/components/images/logo-amblem.png';
import './AuthPanel.scss';

interface Props {
  /** Small uppercased eyebrow above the heading. Optional — omit on
   *  state-card variants (e.g. "link missing") where the kicker would
   *  be redundant with the heading. */
  kicker?: string;
  /** Main heading (h1). Single line, sentence case. */
  heading: string;
  /** Helper copy under the heading. ReactNode (not string) so consumers
   *  can inline counts / em tags without us inventing a template API. */
  subtitle?: ReactNode;
  /** Body slot — fields, errors, buttons, strength checklist, etc.
   *  AuthPanel doesn't prescribe a layout; children stack vertically
   *  with the same gap the auth-form scss used to apply. */
  children: ReactNode;
  /** Bottom "alt" slot — usually a sign-up / sign-in link or a "back"
   *  link. Renders below a thin separator line so it visually splits
   *  from the form body. */
  footer?: ReactNode;
}

/**
 * Shared chrome for the dark-substrate auth pages
 * (Login / ForgotPassword / ResetPassword).
 *
 * Provides:
 *   - The dark `rgba(13,22,38,0.72)` card with the brand shadow + 1px
 *     border + max-width 420px
 *   - Mobile-only emblem chip + brand halo (hidden on the auth desktop
 *     layout where the EmblemShowcase column owns the brand anchor)
 *   - Kicker + heading + subtitle stack (centred)
 *   - Dark-surface overrides for the generic <Input> field
 *     (`.input-field__label` + `.input-field__input` colours)
 *   - An optional footer slot under a thin separator
 *
 * What it intentionally does NOT own (kept consumer-rendered so each
 * page can compose freely):
 *   - The submit button (each form picks its own label + loading state)
 *   - The "trust" strap under the button (the icon + short copy vary
 *     per page — single-use TLS chip for sign-in, "single-use token"
 *     for password reset, etc.)
 *   - Error blocks (each form already has its own inline error layout)
 *   - The success / confirmation / "link invalid" state cards — each
 *     of those is just different `children`
 */
export const AuthPanel = ({ kicker, heading, subtitle, children, footer }: Props) => (
  <div className="auth-panel">
    <div className="auth-panel__emblem-chip" aria-hidden="true">
      <span className="auth-panel__emblem-halo" />
      <img src={logoEmblem} alt="" className="auth-panel__emblem" />
    </div>

    <div className="auth-panel__head">
      {kicker && <span className="auth-panel__kicker">{kicker}</span>}
      <h2 className="auth-panel__heading">{heading}</h2>
      {subtitle && <p className="auth-panel__subtitle">{subtitle}</p>}
    </div>

    {children}

    {footer && <div className="auth-panel__footer">{footer}</div>}
  </div>
);
